import { ReactNode, useRef } from "react";
import { Radio, Checkbox } from "@mui/material";
import { useTranslations } from "next-intl";
import { MultipleChoiceQuestion as MultipleChoiceQuestionType } from "@/commonTypes/question";

export interface MultipleChoiceQuestionProps {
  question: MultipleChoiceQuestionType;
  selectedAnswer?: string | string[] | null;
  onAnswerChange: (questionId: string, answerId: string | string[]) => void;
  onAutoAdvance?: () => void;
  infoToast?: ReactNode;
}

export default function MultipleChoiceQuestion({
  question,
  selectedAnswer = null,
  onAnswerChange,
  onAutoAdvance,
  infoToast,
}: MultipleChoiceQuestionProps) {
  const t = useTranslations();
  const {
    questionId,
    questionTranslationKey,
    bodyTextTranslationKey,
    answers: rawAnswers,
    allowMultipleAnswers = false,
  } = question;

  const questionText = t(questionTranslationKey);
  const bodyText = bodyTextTranslationKey ? t(bodyTextTranslationKey) : null;
  const answers = rawAnswers.map((answer) => ({
    ...answer,
    translatedText: t(answer.translationKey),
    translatedDescription: answer.descriptionTranslationKey ? t(answer.descriptionTranslationKey) : null,
  }));
  const autoAdvanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleAnswerSelect = (answerId: string) => {
    if (allowMultipleAnswers) {
      // Handle multiple selection
      const currentSelections = Array.isArray(selectedAnswer)
        ? selectedAnswer
        : [];

      const selectedAnswerObj = rawAnswers.find((a) => a.id === answerId);
      const isExclusive = selectedAnswerObj?.exclusive === true;
      const isCurrentlySelected = currentSelections.includes(answerId);

      let newSelections: string[];

      if (isCurrentlySelected) {
        // Deselecting an answer - just remove it
        newSelections = currentSelections.filter((id) => id !== answerId);
      } else if (isExclusive) {
        // Selecting an exclusive answer - clear all others
        onAnswerChange(questionId, [answerId]);
        return;
      } else {
        // Selecting a non-exclusive answer - remove any exclusive answers
        const nonExclusiveSelections = currentSelections.filter((id) => {
          const answer = rawAnswers.find((a) => a.id === id);
          return answer?.exclusive !== true;
        });
        newSelections = [...nonExclusiveSelections, answerId];
      }

      onAnswerChange(questionId, newSelections);
    } else {
      // Handle single selection
      onAnswerChange(questionId, answerId);
      if (onAutoAdvance) {
        if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
        autoAdvanceTimer.current = setTimeout(onAutoAdvance, 400);
      }
    }
  };

  const isAnswerSelected = (answerId: string): boolean => {
    if (allowMultipleAnswers) {
      return Array.isArray(selectedAnswer) && selectedAnswer.includes(answerId);
    }
    return selectedAnswer === answerId;
  };

  return (
    <div className="question-container">
      <h1 className="question-title">{questionText}</h1>
      {bodyText && <p className="question-subtitle">{bodyText}</p>}

      {infoToast}

      <div className="answers-container">
        {answers.map((answer) => {
          const isSelected = isAnswerSelected(answer.id);
          return (
            <div
              key={answer.id}
              className={`answer-button ${isSelected ? "selected" : ""} ${answer.translatedDescription ? "has-description" : ""}`}
              onClick={() => handleAnswerSelect(answer.id)}
              role={allowMultipleAnswers ? "checkbox" : "radio"}
              aria-checked={isSelected}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleAnswerSelect(answer.id);
                }
              }}
            >
              {allowMultipleAnswers ? (
                <Checkbox
                  checked={isSelected}
                  readOnly
                  slotProps={{ input: { "aria-label": answer.translatedDescription ? `${answer.translatedText}: ${answer.translatedDescription}` : answer.translatedText } }}
                />
              ) : (
                <Radio
                  checked={isSelected}
                  readOnly
                  slotProps={{ input: { "aria-label": answer.translatedDescription ? `${answer.translatedText}: ${answer.translatedDescription}` : answer.translatedText } }}
                />
              )}
              <span className="answer-text">
                <span className="answer-label">{answer.translatedText}</span>
                {answer.translatedDescription && (
                  <span className="answer-description">{answer.translatedDescription}</span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
